import React from "react";
import { Link } from "react-router-dom";
import "./styles/LegalPage.css";

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <span className="legal-header-brand">Excavate Research and Analysis Pvt Ltd</span>
        <Link to="/" className="legal-back-link">&#8592; Back to Login</Link>
      </header>

      <div className="legal-body">
        <h1 className="legal-doc-title">Privacy Policy</h1>
        <p className="legal-doc-meta">Effective: 17 May 2026</p>

        <div className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Excavate Research and Analysis Pvt Ltd ("Excavate", "we", "us", or "our") is a market research
            and survey data processing company incorporated in India with its registered office at NDM1,
            Netaji Subhash Place, Delhi 110034, India. We provide survey programming, data processing,
            tabulation, and analytics services to clients globally.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, share, and protect personal data when
            you (a) visit our website at{" "}
            <a href="https://pythontest.excavateservices.com" target="_blank" rel="noreferrer">
              https://pythontest.excavateservices.com
            </a>
            , (b) access client-facing applications, portals, or dashboards we operate, or (c) sign in to
            our systems using Single Sign-On (SSO) such as Microsoft Entra (Azure Active Directory).
          </p>
          <p>
            We are committed to handling personal data in accordance with applicable laws, including the
            Digital Personal Data Protection Act, 2023 (India), the EU General Data Protection Regulation
            (GDPR), the UK Data Protection Act 2018, the Australian Privacy Act 1988 (including the
            Australian Privacy Principles), and the Personal Information Protection and Electronic Documents
            Act (PIPEDA, Canada), as applicable.
          </p>
        </div>

        <div className="legal-section">
          <h2>2. Who this policy applies to</h2>
          <p>This policy applies to:</p>
          <ul>
            <li>Client representatives who access our applications, portals, or shared resources via SSO or other authenticated access;</li>
            <li>Visitors to our website;</li>
            <li>Individuals whose personal data is shared with us by our clients or partners in connection with our services.</li>
          </ul>
          <p>
            Where we process survey respondent data on behalf of a client, we generally act as a data
            processor (or equivalent). The client (typically the research commissioner or panel provider) is
            the data controller and their privacy notice governs the respondent's relationship with the project.
          </p>
        </div>

        <div className="legal-section">
          <h2>3. Personal data we collect</h2>
          <h3>3.1 Data you provide directly</h3>
          <ul>
            <li>Identity and contact data: name, business email, phone number, employer, job title.</li>
            <li>Account data: username, authentication identifiers, password hashes (where applicable).</li>
            <li>Communications: messages, support tickets, or feedback you send to us.</li>
          </ul>

          <h3>3.2 Data received via Microsoft SSO</h3>
          <p>
            When you sign in using Microsoft SSO through our tenant (excavateanalysis.onmicrosoft.com),
            Microsoft shares the following profile information with us, subject to your consent on the
            Microsoft permissions screen:
          </p>
          <ul>
            <li>Your name, work email address, and profile photo (if set);</li>
            <li>A unique tenant and object identifier Microsoft assigns to your account;</li>
            <li>Audit and sign-in metadata (date, time, IP, success/failure) generated by Microsoft within our tenant for security and compliance logging.</li>
          </ul>
          <p>
            We do not receive your Microsoft password, the contents of your mailbox, your calendar, your
            OneDrive files, or any other data from your home organisation unless you explicitly upload or
            share it with us.
          </p>

          <h3>3.3 Data collected automatically</h3>
          <ul>
            <li>Technical data: IP address, browser type, device identifiers, operating system, time zone.</li>
            <li>Usage data: pages visited, features used, timestamps, referring URLs, error logs.</li>
            <li>Cookies and similar technologies (see Section 11).</li>
          </ul>

          <h3>3.4 Survey project data</h3>
          <p>
            Where clients engage us to programme surveys, process data, or produce tabulations, we may
            process personal data contained in survey datasets (such as respondent demographics, responses,
            and identifiers). This data is processed strictly on the client's instructions and under the
            contract with that client. We do not use respondent data for our own purposes.
          </p>
        </div>

        <div className="legal-section">
          <h2>4. How we use personal data</h2>
          <p>We use personal data for the following purposes:</p>
          <ul>
            <li>Service delivery: to authenticate users, provide access to dashboards and portals, deliver survey data processing, generate tabulations, and share reports with the relevant client.</li>
            <li>Security and audit logging: to detect and prevent unauthorised access, investigate incidents, and meet contractual security obligations to our clients.</li>
            <li>Account administration: to create, manage, suspend, or remove user accounts and to communicate operationally with users.</li>
            <li>Legal and regulatory compliance: to comply with applicable laws, respond to lawful requests, and enforce our agreements.</li>
            <li>Service improvement: to analyse aggregated, non-identifying usage patterns to improve performance, reliability, and user experience.</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>5. Legal bases for processing</h2>
          <p>Where the GDPR or UK GDPR applies, we rely on the following legal bases:</p>
          <ul>
            <li>Contract: processing necessary to perform our contract with you or with the client that engaged us.</li>
            <li>Legitimate interests: processing necessary for security, fraud prevention, and operating and improving our services, balanced against your rights.</li>
            <li>Consent: where you have given explicit consent, including consent recorded via the Microsoft SSO permissions screen.</li>
            <li>Legal obligation: processing necessary to meet legal or regulatory requirements.</li>
          </ul>
          <p>
            Under the Digital Personal Data Protection Act, 2023 (India), we rely on your consent and on
            legitimate uses recognised by the Act.
          </p>
        </div>

        <div className="legal-section">
          <h2>6. How we share personal data</h2>
          <p>We do not sell personal data. We share it only with:</p>
          <ul>
            <li>The client who engaged the service: for example, the client whose survey you are responding to or whose portal you are accessing.</li>
            <li>Service providers and sub-processors: cloud hosting (e.g., Microsoft Azure), authentication (Microsoft Entra), email and communications providers, analytics and error-monitoring providers, all under written contracts requiring appropriate confidentiality and security.</li>
            <li>Professional advisors: auditors, lawyers, and accountants, under duties of confidentiality.</li>
            <li>Authorities: where required by law, regulation, court order, or to protect our or others' rights.</li>
            <li>Business transfers: in the event of a merger, acquisition, or sale of assets, where personal data may be transferred subject to equivalent protections.</li>
          </ul>
          <p>A current list of material sub-processors is available on request to <a href="mailto:privacy@excavateservices.com">privacy@excavateservices.com</a>.</p>
        </div>

        <div className="legal-section">
          <h2>7. International transfers</h2>
          <p>
            Excavate is based in India, and our clients and respondents are located in countries including
            India, Australia, New Zealand, Canada, the United States, the United Kingdom, and the European
            Economic Area. Personal data may therefore be transferred across borders.
          </p>
          <p>
            Where personal data originating in the EEA or UK is transferred to a country that has not been
            recognised as providing an adequate level of protection, we rely on appropriate safeguards such
            as the Standard Contractual Clauses issued by the European Commission (or the UK International
            Data Transfer Addendum), supplemented where necessary by additional technical and organisational
            measures.
          </p>
          <p>
            For transfers governed by Australian or Canadian law, we take reasonable steps to ensure that
            overseas recipients handle personal data in a manner consistent with the Australian Privacy
            Principles or PIPEDA, as applicable.
          </p>
        </div>

        <div className="legal-section">
          <h2>8. Data retention</h2>
          <p>We retain personal data only for as long as necessary to fulfil the purposes for which it was collected, including legal, accounting, audit, or reporting requirements.</p>
          <ul>
            <li>Account and access logs: typically up to 24 months after the end of the relationship, unless a longer period is required by contract or law.</li>
            <li>Survey project data: retained for the period agreed with the client and deleted or returned in accordance with the contract.</li>
            <li>Website analytics: retained in aggregated form; identifiable raw logs are typically deleted within 90 days.</li>
          </ul>
          <p>Specific retention periods for a project can be confirmed by contacting <a href="mailto:privacy@excavateservices.com">privacy@excavateservices.com</a>.</p>
        </div>

        <div className="legal-section">
          <h2>9. Your rights</h2>
          <p>Subject to applicable law, you have the following rights in relation to your personal data:</p>
          <ul>
            <li>Access: to obtain confirmation of whether we process your data and a copy of that data.</li>
            <li>Correction: to ask us to correct inaccurate or incomplete data.</li>
            <li>Erasure: to ask us to delete your data, subject to legal and contractual exceptions.</li>
            <li>Restriction and objection: to ask us to limit or stop certain processing.</li>
            <li>Portability: to receive data you provided to us in a structured, commonly used, machine-readable format.</li>
            <li>Withdraw consent: where we rely on consent, you may withdraw it at any time without affecting prior processing.</li>
            <li>Complaint: to lodge a complaint with the Data Protection Board of India, the UK Information Commissioner's Office, your EEA supervisory authority, the Office of the Australian Information Commissioner, or the Office of the Privacy Commissioner of Canada, as applicable.</li>
          </ul>
          <p>
            Where the data we hold was provided by our client (for example, your employer or a panel
            provider), we may direct your request to that client and assist them in responding.
          </p>
          <p>To exercise these rights, contact us at <a href="mailto:privacy@excavateservices.com">privacy@excavateservices.com</a>. We will respond within the timeframes required by applicable law.</p>
        </div>

        <div className="legal-section">
          <h2>10. Security</h2>
          <p>We maintain appropriate technical and organisational measures to protect personal data, including:</p>
          <ul>
            <li>Encryption in transit (TLS) and, where appropriate, at rest;</li>
            <li>Role-based access controls and the principle of least privilege;</li>
            <li>Multi-factor authentication for administrative and privileged access;</li>
            <li>Audit logging of access to systems containing personal data;</li>
            <li>Background checks and confidentiality obligations for personnel;</li>
            <li>Documented incident-response procedures and breach notification processes.</li>
          </ul>
          <p>
            No system is perfectly secure. If we become aware of a personal data breach affecting you, we
            will notify you and the relevant authorities where required by law.
          </p>
        </div>

        <div className="legal-section">
          <h2>11. Cookies and similar technologies</h2>
          <p>
            Our website and portals use cookies and similar technologies to enable core functionality (such
            as session management and authentication), to remember preferences, and to measure usage. You
            can control non-essential cookies through your browser settings or, where available, the cookie
            banner on our site. Disabling essential cookies may prevent parts of the service from
            functioning.
          </p>
        </div>

        <div className="legal-section">
          <h2>12. Children</h2>
          <p>
            Our services are intended for business users and are not directed to children under 18. We do
            not knowingly collect personal data from children. Survey programmes that involve minors are
            conducted only on behalf of clients with appropriate parental or guardian consent procedures in
            place.
          </p>
        </div>

        <div className="legal-section">
          <h2>13. Changes to this Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post the updated version on{" "}
            <a href="https://pythontest.excavateservices.com" target="_blank" rel="noreferrer">
              https://pythontest.excavateservices.com
            </a>{" "}
            and update the "Effective" date above. For material changes, we will provide additional notice
            where appropriate (for example, by email or in-product notification).
          </p>
        </div>

        <div className="legal-section">
          <h2>14. Contact us</h2>
          <p>If you have questions, concerns, or requests regarding this Privacy Policy or our handling of your personal data, please contact us:</p>
          <div className="legal-contact-block">
            Excavate Research and Analysis Pvt Ltd{"\n"}
            Attn: Data Protection / Privacy Office{"\n"}
            NDM1, Netaji Subhash Place, Delhi 110034, India{"\n"}
            Email: <a href="mailto:privacy@excavateservices.com">privacy@excavateservices.com</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
